import { updateDocumentSizes } from './storage-usage.js';
import { VaultStorage } from '../../../engine/crypto/vault-storage.js';
/**
 * Document Properties Modal
 * Shows document title (editable), info, and (future) sharing controls.
 * Opened by clicking the doc title in the header.
 */

import { createModal, showModal, closeModal } from "./modals.js";
import { renderSharingSection, cleanupSharingSection } from "./share-modal.js";
import { mapStorageOf, moveMapToDevice } from "./map-storage.js";
import { canMoveToDevice } from "../../../engine/services/move-to-device.js";
import { knownClearRisk } from "../../../engine/services/device-storage.js";
import { hintText } from "../../../engine/core/server-status.js";
import { escapeAttr, escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";
import { engine } from "../../../engine/index.js";

const MODAL_ID = "doc-properties-modal";

// Stored callbacks (injected via init)
let callbacks = null;

/**
 * Initialize the doc properties modal.
 * @param {Object} cbs - Callbacks:
 *   - getCurrentDocId: () => string
 *   - getDocTitle: (docId) => string
 *   - renameDoc: (docId, newTitle) => Promise<void>
 *   - getServerStatus: () => {level, text, hint} (the header server indicator)
 *   - updateHeaderTitle: () => void
 *   - isLoggedIn: () => boolean
 */
export function init(cbs) {
  callbacks = cbs;
}

/**
 * Open the document properties modal.
 */
export async function open() {
  if (!callbacks) return;

  const docId = callbacks.getCurrentDocId();
  const title = callbacks.getDocTitle(docId) || t("common.untitled");
  // Where the map is stored, and the server connection (header indicators).
  const storage = mapStorageOf(docId);
  const locationText = storage === "local" ? t("app.docProps.location.device") : t("app.docProps.location.online");
  const server = callbacks.getServerStatus?.();
  // Open by its link, not as a member: no history (docs/plans/
  // 2026-10-28-link-sharing.md, choice 4); its sharing says what the link is.
  const viaLink = engine.sharing.link.isLinkMap(docId);

  const content = `
    <div class="doc-properties-section">
      <h3>${escapeHtml(t("common.title"))}</h3>
      <input
        type="text"
        class="doc-properties-title-input"
        id="doc-props-title"
        value="${escapeAttr(title)}"
        spellcheck="false"
      />
    </div>
    <div class="doc-properties-section">
      <h3>${escapeHtml(t("app.docProps.info"))}</h3>
      <dl class="doc-properties-info">
        <dt>${escapeHtml(t("app.docProps.location"))}</dt>
        <dd><span>${escapeHtml(locationText)}</span>${storage === "local" && knownClearRisk() === true
          ? `<span class="doc-props-warning">${escapeHtml(t("vault.device.mayBeCleared"))}</span>` : ""}${canMoveToDevice(docId)
          ? `<button type="button" class="doc-props-move-to-device">${escapeHtml(t("vault.map.moveToDevice"))}</button>` : ""}</dd>
        ${server ? `<dt>${escapeHtml(t("app.docProps.connection"))}</dt>
        <dd title="${escapeAttr(hintText(server, t))}">${escapeHtml(t(server.text, server.params))}</dd>` : ""}
        <dt>${escapeHtml(t("app.docProps.storage"))}</dt><dd data-storage-doc-id="${escapeAttr(docId)}" data-size-detail>${escapeHtml(t("app.docProps.loadingSize"))}</dd>
        <dt>${escapeHtml(t("app.docProps.docId"))}</dt>
        <dd style="font-family: monospace; font-size: 11px; word-break: break-all;">${escapeAttr(docId) || "—"}</dd>
      </dl>
    </div>
    ${callbacks.isLoggedIn() && storage !== "local" && !viaLink ? `
    <div class="doc-properties-section">
      <h3>${escapeHtml(t("history.section"))}</h3>
      <p class="doc-props-hint">${escapeHtml(t("history.sectionHint"))}</p>
      <button type="button" class="doc-props-history">${escapeHtml(t("history.menu"))}</button>
    </div>
    ` : ""}
    ${callbacks.isLoggedIn() || viaLink ? `
    <div class="doc-properties-section" id="doc-props-sharing"></div>
    ` : ""}
  `;

  const modal = createModal({
    id: MODAL_ID,
    title: t("app.docProps.title"),
    className: "doc-properties-modal",
    content,
  });

  // Remove any existing instance to prevent stale event handlers
  const existing = document.getElementById(MODAL_ID);
  if (existing) existing.remove();

  document.body.appendChild(modal);
  showModal(MODAL_ID);
  updateDocumentSizes(modal, VaultStorage);

  const cleanup = () => {
    cleanupSharingSection();
    closeModal(MODAL_ID);
    modal.remove();
  };

  // Close on backdrop/X click
  modal.querySelector(".modal-close")?.addEventListener("click", cleanup);
  // Its own confirmation says what the move deletes; the map reopens from this device.
  modal.querySelector(".doc-props-move-to-device")?.addEventListener("click", () => {
    cleanup();
    void moveMapToDevice(docId);
  });
  modal.querySelector(".modal-backdrop")?.addEventListener("click", cleanup);
  // The map's history: its own dialog (interfaces/classic/ui/history.js).
  modal.querySelector(".doc-props-history")?.addEventListener("click", () => {
    cleanup();
    void import("./history.js").then((History) => History.open(docId));
  });

  // Focus title input — use modal.querySelector to avoid stale DOM matches
  const titleInput = modal.querySelector("#doc-props-title");
  if (titleInput) {
    titleInput.focus();
    titleInput.select();

    // Save on blur
    titleInput.addEventListener("blur", () => {
      handleRename(docId, titleInput.value.trim());
    });

    // Save on Enter
    titleInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        titleInput.blur();
      }
    });
  }

  // Render sharing controls inline (if logged in)
  const sharingContainer = modal.querySelector("#doc-props-sharing");
  if (sharingContainer) {
    const sharingHeader = document.createElement("h3");
    sharingHeader.textContent = t("app.docProps.sharing");
    sharingContainer.appendChild(sharingHeader);
    const sharingContent = document.createElement("div");
    sharingContent.className = "share-modal-content"; // enables scoped share CSS
    sharingContainer.appendChild(sharingContent);
    await renderSharingSection(sharingContent, docId, { onClose: cleanup });
  }
}

/**
 * Handle renaming the document.
 */
async function handleRename(docId, newTitle) {
  if (!newTitle || !callbacks) return;

  const currentTitle = callbacks.getDocTitle(docId);
  if (newTitle === currentTitle) return;

  try {
    await callbacks.renameDoc(docId, newTitle);
    callbacks.updateHeaderTitle();
  } catch (err) {
    console.error("[DocProperties] Failed to rename:", err);
  }
}

